import React, { useCallback, useEffect, useState } from "react"
import useApi from "../../hooks/useApi.js"
import GenerateFunnelModal from "./GenerateFunnelModal.jsx"

function SectionHeader({ t, eyebrowKey, titleKey, ledeKey }) {
  return (
    <header className="space-y-2">
      <p
        className="font-mono text-[10px] text-ink-tertiary"
        style={{ letterSpacing: "0.15em", fontVariantNumeric: "tabular-nums" }}
      >
        {t(`dashboard.funnel.${eyebrowKey}`)}
      </p>
      <h2
        className="text-[22px] font-medium text-ink-primary"
        style={{ letterSpacing: "-0.03em" }}
      >
        {t(`dashboard.funnel.${titleKey}`)}
      </h2>
      {ledeKey && (
        <p className="text-sm text-ink-secondary max-w-2xl" style={{ lineHeight: 1.55 }}>
          {t(`dashboard.funnel.${ledeKey}`)}
        </p>
      )}
    </header>
  )
}

function SaveIndicator({ t, saving, savedAt, error }) {
  if (error) return <span className="font-mono text-[11px] text-failure">{error}</span>
  if (saving) return <span className="font-mono text-[11px] text-ink-tertiary">{t("dashboard.funnel.saving")}</span>
  if (savedAt) return <span className="font-mono text-[11px] text-winner">{t("dashboard.funnel.saved")}</span>
  return null
}

function StepEditor({ t, name, steps, active, onSaveFunnel, busy }) {
  const [localName, setLocalName] = useState(name || "")
  const [localSteps, setLocalSteps] = useState(steps)
  const [localActive, setLocalActive] = useState(active ?? true)

  useEffect(() => { setLocalName(name || "") }, [ name ])
  useEffect(() => { setLocalSteps(steps || []) }, [ steps ])
  useEffect(() => { setLocalActive(active ?? true) }, [ active ])

  const updateStep = (idx, patch) => {
    setLocalSteps(localSteps.map((s, i) => i === idx ? { ...s, ...patch } : s))
  }
  const removeStep = (idx) => setLocalSteps(localSteps.filter((_, i) => i !== idx))
  const addStep = () => setLocalSteps([ ...localSteps, { name: "", url_pattern: "" } ])

  const canSave =
    !busy &&
    localName.trim().length > 0 &&
    localSteps.every((s) => (s.name || "").trim() && (s.url_pattern || "").trim())

  const doSave = () => {
    if (!canSave) return
    onSaveFunnel({ name: localName.trim(), active: localActive, steps: localSteps })
  }

  return (
    <div className="space-y-5">
      <div className="bg-surface-card border border-rule rounded-card p-5 space-y-4">
        <div>
          <label className="font-mono text-[10px] text-ink-tertiary" style={{ letterSpacing: "0.10em" }}>
            {t("dashboard.funnel.funnel_name_label").toUpperCase()}
          </label>
          <input
            type="text"
            value={localName}
            onChange={(e) => setLocalName(e.target.value)}
            placeholder={t("dashboard.funnel.funnel_name_placeholder")}
            disabled={busy}
            className="mt-1 w-full bg-surface-page border border-rule rounded-card px-3 py-2 text-[14px] text-ink-primary disabled:opacity-60"
          />
        </div>

        <label className="flex items-center gap-2 text-[13px] text-ink-secondary">
          <input
            type="checkbox"
            checked={localActive}
            onChange={(e) => setLocalActive(e.target.checked)}
            disabled={busy}
          />
          <span className="font-medium">{t("dashboard.funnel.active_label")}</span>
          <span className="text-ink-tertiary">— {t("dashboard.funnel.active_desc")}</span>
        </label>
      </div>

      {localSteps.length === 0 ? (
        <p className="text-[13px] text-ink-tertiary" style={{ lineHeight: 1.55 }}>
          {t("dashboard.funnel.empty")}
        </p>
      ) : (
        <ol className="space-y-2">
          {localSteps.map((step, idx) => (
            <li key={idx} className="flex gap-2 items-center bg-surface-card border border-rule rounded-card px-4 py-3">
              <span
                className="font-mono text-[11px] text-ink-tertiary w-6 text-right"
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                {String(idx).padStart(2, "0")}
              </span>
              <input
                type="text"
                value={step.name || ""}
                onChange={(e) => updateStep(idx, { name: e.target.value })}
                placeholder={t("dashboard.funnel.step_name_placeholder")}
                disabled={busy}
                className="flex-1 bg-surface-page border border-rule rounded-card px-3 py-2 text-[13px] text-ink-primary disabled:opacity-60"
              />
              <input
                type="text"
                value={step.url_pattern || ""}
                onChange={(e) => updateStep(idx, { url_pattern: e.target.value })}
                placeholder={t("dashboard.funnel.step_pattern_placeholder")}
                disabled={busy}
                className="flex-[1.5] bg-surface-page border border-rule rounded-card px-3 py-2 text-[13px] font-mono text-ink-primary disabled:opacity-60"
                style={{ fontVariantNumeric: "tabular-nums" }}
              />
              <button
                type="button"
                onClick={() => removeStep(idx)}
                disabled={busy}
                className="text-[12px] text-ink-tertiary hover:text-failure disabled:opacity-60"
              >
                {t("dashboard.funnel.step_remove")}
              </button>
            </li>
          ))}
        </ol>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={addStep}
          disabled={busy}
          className="text-[13px] text-ai hover:underline disabled:opacity-60"
        >
          + {t("dashboard.funnel.step_add")}
        </button>
        <span className="flex-1" />
        <button
          type="button"
          onClick={doSave}
          disabled={!canSave}
          className="bg-brand text-ink-on-dark rounded-button px-4 py-2 text-[13px] font-medium disabled:opacity-60"
        >
          {busy ? t("dashboard.funnel.saving") : t("dashboard.funnel.save_button")}
        </button>
      </div>
    </div>
  )
}

function DropOffViz({ t, steps }) {
  if (!steps || steps.length === 0 || steps.every((s) => s.visits === 0)) {
    return (
      <p className="text-[13px] text-ink-tertiary" style={{ lineHeight: 1.55 }}>
        {t("dashboard.funnel.stats_empty")}
      </p>
    )
  }

  const firstVisits = steps[0].visits || 0
  const safeWidthPct = (n) => (firstVisits > 0 ? Math.max(2, (n / firstVisits) * 100) : 2)

  return (
    <div className="space-y-3">
      <div className="flex justify-between font-mono text-[10px] text-ink-tertiary" style={{ letterSpacing: "0.10em" }}>
        <span>{t("dashboard.funnel.stats_visits_label")}</span>
        <span>{t("dashboard.funnel.stats_dropoff_label")}</span>
      </div>
      {steps.map((step) => (
        <div key={step.index} className="space-y-1">
          <div className="flex items-baseline justify-between gap-4">
            <div className="flex items-baseline gap-3 min-w-0">
              <span
                className="font-mono text-[11px] text-ink-tertiary"
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                {String(step.index).padStart(2, "0")}
              </span>
              <span className="text-[14px] text-ink-primary truncate">{step.name}</span>
              <code
                className="font-mono text-[11px] text-ink-faint truncate"
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                {step.url_pattern}
              </code>
            </div>
            <div className="flex items-baseline gap-3 shrink-0">
              <span
                className="font-mono text-[13px] text-ink-primary"
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                {step.visits.toLocaleString()}
              </span>
              {step.drop_off_pct != null ? (
                <span
                  className={`font-mono text-[11px] px-[7px] py-[1px] rounded-pill ${
                    step.drop_off_pct >= 60 ? "text-negative" : "text-ink-tertiary"
                  }`}
                  style={{ fontVariantNumeric: "tabular-nums" }}
                >
                  −{step.drop_off_pct}%
                </span>
              ) : (
                <span className="font-mono text-[11px] text-ink-faint w-[52px] text-right">—</span>
              )}
            </div>
          </div>
          <div
            className="h-[6px] rounded-full"
            style={{ background: "var(--color-ai-fill)" }}
          >
            <div
              className="h-full rounded-full"
              style={{
                width: `${safeWidthPct(step.visits)}%`,
                background: "var(--color-ai)",
                transition: "width 200ms ease"
              }}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

export default function FunnelPage({ t }) {
  const api = useApi()
  const [funnel, setFunnel] = useState(null)
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState(null)
  const [error, setError] = useState(null)
  const [showGenerateModal, setShowGenerateModal] = useState(false)

  const load = useCallback(async () => {
    try {
      const [funnelResp, statsResp] = await Promise.all([
        api.get("/api/v1/shop/funnel"),
        api.get("/api/v1/shop/funnel/stats")
      ])
      setFunnel(funnelResp || { name: "", steps: [], active: true })
      setStats(statsResp)
    } catch (err) {
      setError(err.message || "Could not load funnel")
    } finally {
      setLoading(false)
    }
  }, [ api ])

  useEffect(() => { load() }, [ load ])

  useEffect(() => {
    if (!savedAt) return
    const id = setTimeout(() => setSavedAt(null), 2000)
    return () => clearTimeout(id)
  }, [ savedAt ])

  const saveFunnel = async ({ name, active, steps }) => {
    setSaving(true)
    setError(null)
    try {
      await api.put("/api/v1/shop/funnel", { funnel: { name, active, steps } })
      await load()
      setSavedAt(Date.now())
    } catch (err) {
      setError(err.message || t("dashboard.funnel.save_error"))
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <p className="text-sm text-ink-tertiary">…</p>

  return (
    <div className="space-y-12">
      <header className="flex items-baseline justify-between gap-4 flex-wrap">
        <div className="space-y-3">
          <p
            className="font-mono text-[10px] text-ai"
            style={{ letterSpacing: "0.15em", fontVariantNumeric: "tabular-nums" }}
          >
            {t("dashboard.funnel.eyebrow")}
          </p>
          <h1
            className="text-[28px] font-medium text-ink-primary"
            style={{ letterSpacing: "-0.035em" }}
          >
            {t("dashboard.funnel.title")}
          </h1>
          <p className="text-sm text-ink-secondary max-w-2xl" style={{ lineHeight: 1.55 }}>
            {t("dashboard.funnel.lede")}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <SaveIndicator t={t} saving={saving} savedAt={savedAt} error={error} />
          <button
            type="button"
            onClick={() => setShowGenerateModal(true)}
            className="bg-surface-card border border-rule rounded-button px-4 py-2 text-[13px] font-medium text-ink-primary hover:border-ai hover:text-ai"
          >
            {t("dashboard.funnel.generate.button")}
          </button>
        </div>
      </header>

      <section className="space-y-5">
        <SectionHeader t={t} eyebrowKey="editor_eyebrow" titleKey="editor_title" ledeKey="editor_lede" />
        <StepEditor
          t={t}
          name={funnel?.name}
          steps={funnel?.steps || []}
          active={funnel?.active}
          onSaveFunnel={saveFunnel}
          busy={saving}
        />
      </section>

      <section className="space-y-5">
        <SectionHeader t={t} eyebrowKey="stats_eyebrow" titleKey="stats_title" ledeKey="stats_lede" />
        <DropOffViz t={t} steps={stats?.steps || []} />
      </section>

      {showGenerateModal && (
        <GenerateFunnelModal
          t={t}
          onClose={() => setShowGenerateModal(false)}
          onApproved={load}
        />
      )}
    </div>
  )
}
